<template>
    <div class="message-details">
        <div class="contact">
            <small>{{ Language.say('messages.details.from') }}</small>
            <span role="button"
              @click="showContactMenu()"
              v-context-click="{
                action: onContextMenuShow, data: {type: 'from', index: 0}
              }" tabindex="0">
                <span>{{recipients.from.name}}</span>
                <small>&lt;{{recipients.from.email}}&gt;</small>
            </span>
        </div>

        <div class="contact">
            <small>{{ Language.say('messages.details.to') }}</small>
            <span role="button" tabindex="0">
                <span v-context-click="{action: onContextMenuShow, data: {type: 'to', index: 0}}" v-if="recipients.to.length > 0">
                  <span>{{recipients.to[0].name}}</span>
                  <small>&lt;{{recipients.to[0].email}}&gt;</small>
                </span>
                <span v-if="recipients.count > 1"
                    @click="showMoreRecipientsToggle()"
                    v-tooltip='Language.say("messages.open.recipients.details")'
                    class="show-more"
                >
                  <small class="counter">+{{recipients.count - 1}}</small>
                  <i class="fas" :class="{'fa-chevron-up': show_more_recipients, 'fa-chevron-down': !show_more_recipients}"></i>
                </span>
            </span>
        </div>

        <div class="contact-list" v-show="recipients.count > 1 && show_more_recipients === true">
            <div v-if="recipients.to.length > 1" class="contact to">
                <span role="button"
                  v-for="(recipient, recipient_index) in recipients.to" :key='recipient.email'
                  v-context-click="{action: onContextMenuShow, data: {type: 'to', index: recipient_index}}">
                    <span>{{recipient.name}} </span>
                    <small>&lt;{{recipient.email}}&gt;,</small>
                </span>
            </div>

            <div v-if="recipients.cc.length > 0" class="contact">
                <small>{{ Language.say('messages.details.cc') }}</small>
                <span role="button"
                  v-for="(recipient, recipient_index) in recipients.cc" :key='recipient.email'
                  v-context-click="{action: onContextMenuShow, data: {type: 'cc', index: recipient_index}}">
                    <span>{{recipient.name}} </span>
                    <small>&lt;{{recipient.email}}&gt;,</small>
                </span>
            </div>

            <div v-if="recipients.bcc.length > 0" class="contact">
                <small>{{ Language.say('messages.details.bcc') }}</small>
                <span role="button"
                  v-for="(recipient, recipient_index) in recipients.bcc" :key='recipient.email'
                  v-context-click="{action: onContextMenuShow, data: {type: 'bcc', index: recipient_index}}">
                    <span>{{recipient.name}} </span>
                    <small>&lt;{{recipient.email}}&gt;,</small>
                </span>
            </div>
        </div>

        <ContextMenu ref="context_contacts_component">
          <template #menu_items>
              <ContactsContextBoxes
                :context_contact="context_contact"></ContactsContextBoxes>
          </template>
        </ContextMenu>
    </div>
</template>

<script setup>
  import {ref, reactive, defineProps}            from 'vue';
  import {onBeforeMount, onMounted, onUnmounted} from 'vue';
  import ContextMenu                             from '@/vue/Elements/Tools/ContextMenu.vue';
  import ContactsContextBoxes                    from '@/vue/Views/Contacts/Partials/ContactsContextBoxes.vue';
  import ContactsController                      from '@/js/Contacts/ContactsController';
  import ContactsRecipientsCollection            from '@/js/Contacts/Collections/ContactsRecipientsCollection';
  import Language                                from '@/js/Shared/Language/Language.js'

  const props = defineProps({
    recipients : {
      required : true,
    },
  })

  const show_more_recipients       = ref(false);
  const show_contact_menu          = ref(false);
  const contacts_controller        = ContactsController;
  const context_contact            = ref({});
  const context_contacts_component = ref();
  const recipients_from            = reactive(new ContactsRecipientsCollection);
  const recipients_to              = reactive(new ContactsRecipientsCollection);
  const recipients_cc              = reactive(new ContactsRecipientsCollection);
  const recipients_bcc             = reactive(new ContactsRecipientsCollection);

  onBeforeMount(() => {
    setContacts();
    setRecipients()
  });

  onMounted(() => {
    contacts_controller.broadcaster.on('contacts-added-context', resetRecipientCollections);
    contacts_controller.broadcaster.on('contacts-deleted-context', resetRecipientCollections);
  });

  onUnmounted(() => {
    contacts_controller.broadcaster.destroy('contacts-added-context', resetRecipientCollections);
    contacts_controller.broadcaster.destroy('contacts-deleted-context', resetRecipientCollections);
  });

  const showMoreRecipientsToggle = () => {
    show_more_recipients.value = !show_more_recipients.value;
  }

  const showContactMenu = () => {
    show_contact_menu.value = !show_contact_menu.value;
  }

  const onContextMenuShow = (context_object) => {
    const recipient_objects = {
      'from' : recipients_from,
      'to' : recipients_to,
      'cc' : recipients_cc,
      'bcc' : recipients_bcc,
    }

    context_contact.value = recipient_objects[context_object.type].cache_array_index[context_object.index];

    if(!context_contact.value) {
      return;
    }

    context_contacts_component.value.show()
  }

  const setContacts = () => {
    recipients_from.setContacts(contacts_controller.contacts);
    recipients_to.setContacts(contacts_controller.contacts);
    recipients_cc.setContacts(contacts_controller.contacts);
    recipients_bcc.setContacts(contacts_controller.contacts);
  }

  const setRecipients = () => {
    recipients_from.addPersonByArray([props.recipients.from]);
    recipients_to.addPersonByArray(props.recipients.to);
    recipients_cc.addPersonByArray(props.recipients.cc);
    recipients_bcc.addPersonByArray(props.recipients.bcc);
  }


  const resetRecipientCollections = async () => {
    recipients_from.reset();
    recipients_to.reset();
    recipients_cc.reset();
    recipients_bcc.reset();

    setContacts();
    setRecipients();
  }
</script>
